import { useCallback, useMemo, useState } from 'react'
import type { Player } from '@/types/turn'

/**
 * O estado da fila da rodada — puro, testável sem renderizar nada.
 *
 * A fila É a fonte da verdade: quem está nela está marcado, e a posição é a
 * ordem de jogo. A tela antiga guardava a seleção e derivava a ordem da ordem
 * de clique, o que fazia desmarcar e remarcar alguém jogá-lo para o fim junto
 * com surpresas: agora esse comportamento é explícito e testado.
 */
export interface OrdemDaRodada {
  fila: string[]
  estaNaFila: (id: string) => boolean
  posicao: (id: string) => number | null
  alternar: (id: string) => void
  mover: (id: string, direcao: 'cima' | 'baixo') => void
  embaralhar: () => void
  definir: (ids: string[]) => void
  limpar: () => void
}

/** Fisher-Yates. Recebe o sorteio por parâmetro para o teste poder fixá-lo. */
export function embaralhado(ids: string[], sorteio: () => number = Math.random): string[] {
  const copia = [...ids]
  for (let i = copia.length - 1; i > 0; i--) {
    const j = Math.floor(sorteio() * (i + 1))
    ;[copia[i], copia[j]] = [copia[j], copia[i]]
  }
  return copia
}

/**
 * Quem já vem na fila ao abrir a tela: os jogadores presentes.
 *
 * `online` ausente conta como presente — mesa sem dado de presença não pode ter
 * a fila esvaziada por engano. NPC nunca entra sozinho: quem o põe é o mestre.
 */
export function filaInicial(participantes: Player[]): string[] {
  return participantes.filter(p => !p.isNpc && p.online !== false).map(p => p.id)
}

export function useOrdemDaRodada(
  participantes: Player[],
  sorteio: () => number = Math.random,
): OrdemDaRodada {
  const [fila, setFila] = useState<string[]>(() => filaInicial(participantes))

  const naFila = useMemo(() => new Set(fila), [fila])

  const estaNaFila = useCallback((id: string) => naFila.has(id), [naFila])

  const posicao = useCallback((id: string) => {
    const indice = fila.indexOf(id)
    return indice === -1 ? null : indice + 1
  }, [fila])

  // Marcar acrescenta ao FIM; desmarcar tira sem mexer em mais ninguém.
  const alternar = useCallback((id: string) => {
    setFila(atual => atual.includes(id) ? atual.filter(x => x !== id) : [...atual, id])
  }, [])

  const mover = useCallback((id: string, direcao: 'cima' | 'baixo') => {
    setFila(atual => {
      const de = atual.indexOf(id)
      if (de === -1) return atual
      const para = direcao === 'cima' ? de - 1 : de + 1
      if (para < 0 || para >= atual.length) return atual  // bordas não giram
      const nova = [...atual]
      ;[nova[de], nova[para]] = [nova[para], nova[de]]
      return nova
    })
  }, [])

  const embaralhar = useCallback(() => {
    setFila(atual => embaralhado(atual, sorteio))
  }, [sorteio])

  const definir = useCallback((ids: string[]) => setFila([...ids]), [])
  const limpar = useCallback(() => setFila([]), [])

  return { fila, estaNaFila, posicao, alternar, mover, embaralhar, definir, limpar }
}
